import { useMutation } from '@apollo/client'
import React, { useCallback, useContext, useEffect } from 'react'

import {
  GetCustomerCartQuery,
  RemoveAndAddSimpleProductsToCartDocument,
  RemoveItemFromCartDocument,
  UpdateCartItemsDocument,
} from '../../graphql/magento'
import { TokenContext } from '../../layouts/context'
import { setAnalyticsEvent } from '../../lib/analytics/state'
import { parseItemIdAsNumber } from '../../lib/cart/item'
import {
  cartItemsUpdating$,
  setCartItemsUpdating,
} from '../../lib/cart/state/cart-items/cart-item-update'
import { CartItemVirtualProduct } from '../../lib/cart/state/types'
import { CartItem as CartItemType } from '../../lib/cart/types'
import { logAndCaptureException } from '../../utils/errorTools'
import { checkAndDiscardQueryResponse } from '../../utils/graphqlTools'
import { CartItemProps } from './types'

const randomRequestId = (itemId: string) => `${itemId}-${Math.random().toString(32).substr(2, 8)}`

const publishRemoveFromCartEvent = (item: CartItemType) => () => {
  const sku = item.product.sku
  const rowTotal = item.prices?.row_total
  const currency = rowTotal?.currency
  const value = rowTotal?.value
  if (!sku || !currency || value == null) {
    return
  }
  setAnalyticsEvent({
    event: 'remove_from_cart',
    currency,
    value,
    items: [
      {
        item_id: sku,
        item_name: item.product.name ?? undefined,
        currency,
        price: item.prices?.price?.value ?? undefined,
        quantity: item.quantity,
      },
    ],
  })
}

export interface CartItemContainerProps {
  className?: string
  cartId: string
  component: React.ElementType<CartItemProps>
  item: CartItemType
  labelDesignFeeItem?: CartItemVirtualProduct
  requestDispatch(action: { type: 'requestPending' | 'requestComplete'; requestId: string }): void
  shippingAddress: GetCustomerCartQuery['customerCart']['shipping_addresses'][0] | undefined
  isConfirmation?: boolean
  showWarning?: boolean
}

const CartItemContainer: React.FC<CartItemContainerProps> = ({
  cartId,
  className,
  component,
  item,
  labelDesignFeeItem,
  requestDispatch,
  shippingAddress,
  isConfirmation,
  showWarning = false,
}) => {
  const token = useContext(TokenContext)

  const [
    updateCartItems,
    { loading: updateLoading, error: mutationError, client: mutationClient },
  ] = useMutation(UpdateCartItemsDocument, {
    context: { token },
  })

  useEffect(() => {
    if (mutationError?.graphQLErrors) {
      mutationClient.cache.evict({ id: `Cart:${cartId}`, fieldName: 'items' })
    }
  }, [cartId, mutationClient.cache, mutationError])

  const [deleteItem, { loading: deleteLoading, error: deleteErrorData }] = useMutation(
    RemoveItemFromCartDocument,
    {
      context: { token },
    },
  )

  const [
    removeAndAddSimpleProducts,
    { loading: removeAndAddLoading, error: removeAndAddErrorData },
  ] = useMutation(RemoveAndAddSimpleProductsToCartDocument, {
    context: { token },
  })

  const handleDelete = useCallback(async (): Promise<void> => {
    const requestId = randomRequestId(item.id)
    requestDispatch({ type: 'requestPending', requestId })

    // Cart dirty state is now automatically managed by cartItemsUpdating$ observable

    if (item.product.sku) {
      const currentSkus = cartItemsUpdating$.getValue()
      if (!currentSkus.includes(item.product.sku)) {
        setCartItemsUpdating([...currentSkus, item.product.sku])
      }
    }
    return Promise.resolve()
      .then(() => parseItemIdAsNumber(item))
      .then((itemId) =>
        deleteItem({
          variables: {
            cartId,
            itemId,
          },
        }),
      )
      .then(checkAndDiscardQueryResponse)
      .then(publishRemoveFromCartEvent(item))
      .catch((rawErr: unknown) => {
        const err: Error = rawErr instanceof Error ? rawErr : new Error(`handleDelete: ${rawErr}`)
        logAndCaptureException(err)
      })
      .finally(() => {
        requestDispatch({ type: 'requestComplete', requestId })
        if (item.product.sku) {
          setCartItemsUpdating(
            cartItemsUpdating$.getValue().filter((sku) => sku !== item.product.sku),
          )
        }
      })
  }, [cartId, deleteItem, item, requestDispatch])

  const handleRemoveAndAddSimpleProducts = useCallback(
    async (cartItems: Array<{ sku: string; quantity: number }>): Promise<void> => {
      const requestId = randomRequestId(item.id)
      requestDispatch({ type: 'requestPending', requestId })

      // Cart dirty state is now automatically managed by cartItemsUpdating$ observable

      if (item.product.sku) {
        const currentSkus = cartItemsUpdating$.getValue()
        if (!currentSkus.includes(item.product.sku)) {
          setCartItemsUpdating([...currentSkus, item.product.sku])
        }
      }
      return Promise.resolve()
        .then(() => parseItemIdAsNumber(item))
        .then((itemId) =>
          removeAndAddSimpleProducts({
            variables: {
              cartId,
              itemId,
              cartItems: cartItems.map(({ sku, quantity }) => ({ data: { sku, quantity } })),
            },
          }),
        )
        .then(checkAndDiscardQueryResponse)
        .catch((rawErr: unknown) => {
          const err: Error =
            rawErr instanceof Error
              ? rawErr
              : new Error(`handleRemoveAndAddSimpleProducts: ${rawErr}`)
          logAndCaptureException(err)
        })
        .finally(() => {
          requestDispatch({ type: 'requestComplete', requestId })
          if (item.product.sku) {
            setCartItemsUpdating(
              cartItemsUpdating$.getValue().filter((sku) => sku !== item.product.sku),
            )
          }
        })
    },
    [cartId, item, removeAndAddSimpleProducts, requestDispatch],
  )

  useEffect(() => {
    if (deleteErrorData?.graphQLErrors) {
      // Access client from deleteOptions if needed, assuming deleteItem returns it in its result
      // For now, this assumes client is available on deleteErrorData or deleteOptions.client
      // This part might need adjustment based on actual structure of deleteOptions
    }
  }, [cartId, deleteErrorData])

  useEffect(() => {
    if (removeAndAddErrorData?.graphQLErrors) {
      // Similar to deleteErrorData, client access might need review
    }
  }, [cartId, removeAndAddErrorData])

  const handleUpdateCartItem = useCallback(
    async ({
      autoShipInterval,
      orderType,
      quantity,
    }: {
      autoShipInterval?: number
      orderType?: number
      quantity?: number
    }): Promise<{ error?: Error }> => {
      const requestId = randomRequestId(item.id)
      requestDispatch({ type: 'requestPending', requestId })

      // Cart dirty state is now automatically managed by cartItemsUpdating$ observable

      if (item.product.sku) {
        const currentSkus = cartItemsUpdating$.getValue()
        if (!currentSkus.includes(item.product.sku)) {
          setCartItemsUpdating([...currentSkus, item.product.sku])
        }
      }
      return Promise.resolve()
        .then(() => parseItemIdAsNumber(item))
        .then((itemId) =>
          updateCartItems({
            variables: {
              cartId,
              cartItems: [
                {
                  cart_item_id: itemId,
                  quantity: quantity != null ? quantity : item.quantity,
                  ...(autoShipInterval != null && { dfh_autoship_interval: autoShipInterval }),
                  ...(orderType != null && { dfh_order_type: orderType }),
                },
              ],
            },
          }),
        )
        .then(checkAndDiscardQueryResponse)
        .then(() => ({}))
        .catch((rawErr: unknown) => {
          const err: Error =
            rawErr instanceof Error ? rawErr : new Error(`handleUpdateCartItems: ${rawErr}`)
          logAndCaptureException(err)
          return { error: err }
        })
        .finally(() => {
          requestDispatch({ type: 'requestComplete', requestId })
          if (item.product.sku) {
            setCartItemsUpdating(
              cartItemsUpdating$.getValue().filter((sku) => sku !== item.product.sku),
            )
          }
        })
    },
    [cartId, item, requestDispatch, updateCartItems],
  )

  return React.createElement(component, {
    className,
    deleteCartItem: handleDelete,
    deleteError: deleteErrorData,
    item,
    labelDesignFeeItem,
    loading: updateLoading || deleteLoading || removeAndAddLoading,
    shippingAddress,
    updateCartItem: handleUpdateCartItem,
    removeAndAddSimpleProducts: handleRemoveAndAddSimpleProducts,
    removeAndAddError: removeAndAddErrorData,
    isConfirmation,
    showWarning,
  })
}

export default CartItemContainer
